'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { getSupabaseBrowser } from '@/lib/supabase-browser';

interface VerifyOTPPageProps {
  email: string;
  onBack: () => void;
  onSuccess: () => void;
}

export default function VerifyOTPComponent({ email, onBack, onSuccess }: VerifyOTPPageProps) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);



  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (otp.length !== 6) {
      setError('O código deve ter 6 dígitos');
      return;
    }

    setLoading(true);

    try {
      const supabase = getSupabaseBrowser();
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'signup'
      });

      if (error) throw error;

      onSuccess();

    } catch (err: any) {
      setError(err.message || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      setLoading(true);
      const supabase = getSupabaseBrowser();
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email
      });

      if (error) {
        console.error('[OTP] Erro no reenvio:', error.message);
        console.error('- Status:', error.status);
        console.error('- Error completo:', error);
        throw error;
      }

      setError('✅ Novo código enviado! Verifique sua caixa de entrada (e spam).');
    } catch (err: any) {
      setError('Erro ao reenviar código: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold">Confirme seu Email</h1>
        <p className="text-sm text-gray-500 mt-1">
          Digite o código de 6 dígitos enviado para
        </p>
        <p className="text-sm font-medium text-gray-700">
          {email}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleVerifyOTP} className="space-y-4">
        <div className="grid gap-1.5">
          <label className="text-sm font-medium">Código de Verificação</label>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-12 w-full rounded-md border border-gray-300 px-3 text-center text-lg font-mono tracking-widest outline-none focus:ring-2 focus:ring-black/10"
            placeholder="000000"
            maxLength={6}
            required
          />
          <p className="text-xs text-gray-500">
            Digite os 6 dígitos recebidos por email
          </p>
        </div>

        <button
          type="submit"
          disabled={loading || otp.length !== 6}
          className="h-10 w-full rounded-md bg-black text-white font-medium hover:bg-black/90 disabled:opacity-50"
        >
          {loading ? 'Verificando...' : 'Confirmar Código'}
        </button>
      </form>

      <div className="mt-4 text-center text-sm text-gray-600 space-y-2">
        <button
          onClick={handleResendOTP}
          disabled={loading}
          className="underline underline-offset-4 text-blue-600"
          type="button"
        >
          Reenviar código
        </button>
        <br />
        <button
          onClick={onBack}
          className="underline underline-offset-4"
          type="button"
        >
          Voltar
        </button>
      </div>
    </div>
  );
}